import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories, articles } from '@/mocks/data';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];
  const [activeSlug, setActiveSlug] = useState(currentCategory.slug);

  useEffect(() => {
    if (slug && categories.some((c) => c.slug === slug)) {
      setActiveSlug(slug);
    }
  }, [slug]);

  const filteredArticles = useMemo(
    () => articles.filter((a) => a.category === activeSlug),
    [activeSlug]
  );

  const activeCategory = categories.find((c) => c.slug === activeSlug) || categories[0];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${activeCategory.name} - 久久在线`,
    description: activeCategory.description,
    url: `https://www.jiujiuzaixian.com/category/${activeCategory.slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '久久在线',
      url: 'https://www.jiujiuzaixian.com',
    },
  };

  return (
    <>
      <ScrollToTop />
      <SEO
        title={`${activeCategory.name} - 久久在线 | 久久在线官网`}
        description={activeCategory.description}
        keywords={`久久在线,${activeCategory.name},在线服务,久久在线${activeCategory.name}`}
        canonicalUrl={`https://www.jiujiuzaixian.com/category/${activeCategory.slug}`}
      />
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>

      <Navbar />
      <main className="pt-20 md:pt-24">
        <div className="w-full px-4 md:px-6 py-8">
          <div className="max-w-7xl mx-auto">
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: activeCategory.name },
                ]}
              />
            </div>

            <div className="mb-8">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                {activeCategory.name}
              </h1>
              <p className="text-sm md:text-base text-foreground-600">{activeCategory.description}</p>
            </div>

            <div className="flex flex-wrap gap-2 mb-10">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => setActiveSlug(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {filteredArticles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {filteredArticles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    className="group rounded-2xl bg-background-50 border border-background-200/70 hover:border-primary-200 overflow-hidden transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={article.coverImage}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-3 text-xs text-foreground-500 mb-3">
                        <span>{article.publishedAt}</span>
                        <span>·</span>
                        <span>{article.readTime}阅读</span>
                      </div>
                      <h3 className="text-base font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
                        {article.summary}
                      </p>
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {article.tags.map((tag) => (
                          <span
                            key={tag}
                            className="px-2.5 py-0.5 rounded-full bg-background-100 text-xs text-foreground-500"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                  <i className="ri-inbox-line text-2xl"></i>
                </div>
                <p className="text-foreground-500">该分类暂无文章</p>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}